/**
 * API service for company knowledge base operations
 */

// =============================================================================
// TYPES
// =============================================================================

export type CompanyDocumentCategory =
  | 'company_overview'
  | 'mission_vision'
  | 'organizational_chart'
  | 'company_history'
  | 'team_cv'
  | 'key_personnel'
  | 'team_structure'
  | 'qualifications'
  | 'case_study'
  | 'project_description'
  | 'client_reference'
  | 'testimonial'
  | 'technical_specification'
  | 'methodology'
  | 'technology_stack'
  | 'integration_capability'
  | 'security_compliance'
  | 'iso_certification'
  | 'industry_accreditation'
  | 'quality_assurance'
  | 'insurance_certificate'
  | 'safety_certification'
  | 'financial_statement'
  | 'annual_report'
  | 'bank_reference'
  | 'insurance_coverage'
  | 'health_safety_policy'
  | 'environmental_policy'
  | 'equality_diversity_policy'
  | 'modern_slavery_statement'
  | 'data_protection_policy'
  | 'business_continuity_plan'
  // NEW ENHANCED CATEGORIES
  | 'past_performance'
  | 'capability_statement'
  | 'risk_register'
  | 'innovation_case'
  | 'social_value_evidence'
  | 'capacity_statement'
  | 'other';

export type CompanyDocumentStatus = 'active' | 'expired' | 'pending_review' | 'archived';

export interface CompanyDocument {
  id: string;
  company_id: string;
  user_id: string;
  title: string;
  category: CompanyDocumentCategory;
  subcategory?: string;
  content: string;
  summary?: string;
  key_points: string[];
  file_url?: string;
  file_name?: string;
  file_type?: string;
  file_size_bytes?: number;
  tags: string[];
  keywords: string[];
  status: CompanyDocumentStatus;
  valid_from?: string;
  valid_until?: string;
  // Personnel fields
  person_name?: string;
  person_role?: string;
  person_qualifications: string[];
  years_experience?: number;
  // Project fields
  project_name?: string;
  project_client?: string;
  project_value?: string;
  project_duration?: string;
  project_outcomes: string[];
  project_industry?: string;
  // Certification fields
  certification_issuer?: string;
  certification_number?: string;
  certification_scope?: string;
  // Financial fields
  financial_year?: string;
  financial_figures: Record<string, any>;
  // NEW ENHANCED METADATA FIELDS
  // Performance Metrics
  success_metrics: string[];
  performance_kpis: Record<string, any>;
  // Tender Relevance
  tender_relevance_tags: string[];
  contract_value_bands: string[];
  geographic_relevance: string[];
  question_type_mapping: string[];
  // Risk Register fields
  risk_type?: string;
  risk_impact?: string;
  mitigation_strategy?: string;
  risk_outcome?: string;
  // Innovation fields
  innovation_type?: string;
  innovation_impact: string[];
  innovation_adoption?: string;
  // Social Value fields
  social_value_type?: string;
  social_value_impact: string[];
  beneficiary_groups: string[];
  // Capacity fields
  resource_count?: number;
  scalability_factor?: string;
  concurrent_projects?: number;
  // Analytics
  usage_count: number;
  last_used_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CompanyDocumentCreateRequest {
  title: string;
  category: CompanyDocumentCategory;
  subcategory?: string;
  content: string;
  summary?: string;
  key_points?: string[];
  file_url?: string;
  file_name?: string;
  file_type?: string;
  file_size_bytes?: number;
  tags?: string[];
  valid_from?: string;
  valid_until?: string;
  // Category-specific
  person_name?: string;
  person_role?: string;
  person_qualifications?: string[];
  years_experience?: number;
  project_name?: string;
  project_client?: string;
  project_value?: string;
  project_duration?: string;
  project_outcomes?: string[];
  project_industry?: string;
  certification_issuer?: string;
  certification_number?: string;
  certification_scope?: string;
  financial_year?: string;
  financial_figures?: Record<string, any>;
  // NEW ENHANCED METADATA FIELDS
  success_metrics?: string[];
  performance_kpis?: Record<string, any>;
  tender_relevance_tags?: string[];
  contract_value_bands?: string[];
  geographic_relevance?: string[];
  question_type_mapping?: string[];
  risk_type?: string;
  risk_impact?: string;
  mitigation_strategy?: string;
  risk_outcome?: string;
  innovation_type?: string;
  innovation_impact?: string[];
  innovation_adoption?: string;
  social_value_type?: string;
  social_value_impact?: string[];
  beneficiary_groups?: string[];
  resource_count?: number;
  scalability_factor?: string;
  concurrent_projects?: number;
}

export interface CompanyDocumentUpdateRequest {
  title?: string;
  category?: CompanyDocumentCategory;
  subcategory?: string;
  content?: string;
  summary?: string;
  key_points?: string[];
  tags?: string[];
  status?: CompanyDocumentStatus;
  valid_from?: string;
  valid_until?: string;
  // Category-specific fields
  person_name?: string;
  person_role?: string;
  person_qualifications?: string[];
  years_experience?: number;
  project_name?: string;
  project_client?: string;
  project_value?: string;
  project_duration?: string;
  project_outcomes?: string[];
  project_industry?: string;
  certification_issuer?: string;
  certification_number?: string;
  certification_scope?: string;
  financial_year?: string;
  financial_figures?: Record<string, any>;
  // NEW ENHANCED METADATA FIELDS
  success_metrics?: string[];
  performance_kpis?: Record<string, any>;
  tender_relevance_tags?: string[];
  contract_value_bands?: string[];
  geographic_relevance?: string[];
  question_type_mapping?: string[];
  risk_type?: string;
  risk_impact?: string;
  mitigation_strategy?: string;
  risk_outcome?: string;
  innovation_type?: string;
  innovation_impact?: string[];
  innovation_adoption?: string;
  social_value_type?: string;
  social_value_impact?: string[];
  beneficiary_groups?: string[];
  resource_count?: number;
  scalability_factor?: string;
  concurrent_projects?: number;
}

export interface CompanyProfile {
  id: string;
  user_id: string;
  company_name: string;
  trading_name?: string;
  registration_number?: string;
  vat_number?: string;
  duns_number?: string;
  company_overview?: string;
  mission_statement?: string;
  vision_statement?: string;
  core_values: string[];
  unique_selling_points: string[];
  primary_industry?: string;
  secondary_industries: string[];
  specializations: string[];
  services_offered: string[];
  employee_count?: string;
  year_established?: number;
  company_type?: string;
  ownership_structure?: string;
  headquarters_address?: string;
  headquarters_country?: string;
  office_locations: string[];
  geographic_coverage: string[];
  website?: string;
  main_email?: string;
  main_phone?: string;
  annual_turnover?: string;
  financial_year_end?: string;
  active_certifications: string[];
  accreditations: string[];
  key_contacts: Array<{ name: string; role: string; email: string }>;
  // NEW ENHANCED PROFILE FIELDS
  // Performance Track Record
  performance_summary?: string;
  on_time_delivery_rate?: number;
  budget_adherence_rate?: number;
  client_satisfaction_score?: number;
  key_performance_achievements: string[];
  // Capabilities & Services
  capabilities_overview?: string;
  service_categories: Array<{ category: string; services: string[]; experience_years: number }>;
  technical_capabilities: string[];
  sector_experience: Array<{ sector: string; years: string; project_count: string }>;
  // Risk Management
  risk_management_approach?: string;
  risk_mitigation_examples: string[];
  // Innovation
  innovation_approach?: string;
  recent_innovations: Array<{ title: string; year: string; impact: string }>;
  // Social Value
  social_value_commitments: string[];
  community_initiatives: string[];
  sustainability_commitments: string[];
  // Delivery Capacity
  current_capacity?: string;
  scalability_description?: string;
  resource_availability: Record<string, any>;
  // System fields
  setup_completed: boolean;
  setup_progress: Record<string, boolean>;
  document_counts: Record<string, number>;
  total_documents: number;
  last_document_added?: string;
  created_at: string;
  updated_at: string;
}

export interface CompanyProfileUpdateRequest {
  company_name?: string;
  trading_name?: string;
  registration_number?: string;
  vat_number?: string;
  duns_number?: string;
  company_overview?: string;
  mission_statement?: string;
  vision_statement?: string;
  core_values?: string[];
  unique_selling_points?: string[];
  primary_industry?: string;
  secondary_industries?: string[];
  specializations?: string[];
  services_offered?: string[];
  employee_count?: string;
  year_established?: number;
  company_type?: string;
  ownership_structure?: string;
  headquarters_address?: string;
  headquarters_country?: string;
  office_locations?: string[];
  geographic_coverage?: string[];
  website?: string;
  main_email?: string;
  main_phone?: string;
  annual_turnover?: string;
  financial_year_end?: string;
  active_certifications?: string[];
  accreditations?: string[];
  key_contacts?: Array<{ name: string; role: string; email: string }>;
  // NEW ENHANCED PROFILE FIELDS
  performance_summary?: string;
  on_time_delivery_rate?: number;
  budget_adherence_rate?: number;
  client_satisfaction_score?: number;
  key_performance_achievements?: string[];
  capabilities_overview?: string;
  service_categories?: Array<{ category: string; services: string[]; experience_years: number }>;
  technical_capabilities?: string[];
  sector_experience?: Array<{ sector: string; years: string; project_count: string }>;
  risk_management_approach?: string;
  risk_mitigation_examples?: string[];
  innovation_approach?: string;
  recent_innovations?: Array<{ title: string; year: string; impact: string }>;
  social_value_commitments?: string[];
  community_initiatives?: string[];
  sustainability_commitments?: string[];
  current_capacity?: string;
  scalability_description?: string;
  resource_availability?: Record<string, any>;
  setup_completed?: boolean;
  setup_progress?: Record<string, boolean>;
}

export interface CategoryInfo {
  category: string;
  display_name: string;
  description: string;
  group: string;
  document_count: number;
  recommended_minimum: number;
  critical?: boolean;
}

export interface SetupProgress {
  total_categories: number;
  completed_categories: number;
  completion_percentage: number;
  categories_by_group: Record<string, CategoryInfo[]>;
  missing_critical_categories: string[];
  recommendations: string[];
}

export interface KnowledgeBaseStats {
  total_documents: number;
  documents_by_category: Record<string, number>;
  documents_by_status: Record<string, number>;
  most_used_documents: Array<{
    id: string;
    title: string;
    category: string;
    usage_count: number;
  }>;
  recently_added: Array<{
    id: string;
    title: string;
    category: string;
    created_at: string;
  }>;
  expiring_soon: Array<{
    id: string;
    title: string;
    category: string;
    valid_until: string;
  }>;
  setup_completion_percentage: number;
  categories_with_documents: string[];
  categories_missing_documents: string[];
}

export interface KnowledgeRetrievalRequest {
  question_text: string;
  question_type?: string;
  categories?: CompanyDocumentCategory[];
  max_results?: number;
  min_relevance_score?: number;
  include_expired?: boolean;
}

export interface KnowledgeRetrievalResult {
  document_id: string;
  document_title: string;
  category: CompanyDocumentCategory;
  relevance_score: number;
  matched_content: string;
  full_content: string;
  key_points: string[];
  metadata: Record<string, any>;
}

export interface KnowledgeRetrievalResponse {
  question_text: string;
  results: KnowledgeRetrievalResult[];
  total_matches: number;
  search_categories: string[];
  processing_time_ms: number;
}

export interface DocumentListResponse {
  documents: CompanyDocument[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface BulkUploadResponse {
  created_count: number;
  failed_count: number;
  created_ids: string[];
  errors: Array<{ index: number; title: string; error: string }>;
}

export interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// =============================================================================
// API CONFIGURATION
// =============================================================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL}/api`
  : process.env.NODE_ENV === 'production'
  ? 'https://api-beta-open-tender.nat-quest.com/api'
  : 'http://localhost:2024/api';

const getAuthToken = (): string | null => {
  try {
    const stored = localStorage.getItem('opentender_tokens');
    if (!stored) return null;
    const { accessToken } = JSON.parse(stored);
    return accessToken;
  } catch {
    return localStorage.getItem('access_token');
  }
};

const createHeaders = (includeContentType = true): HeadersInit => {
  const headers: HeadersInit = {};
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const handleResponse = async <T>(response: Response): Promise<APIResponse<T>> => {
  if (response.ok) {
    try {
      const data = await response.json();
      return { success: true, data };
    } catch {
      return { success: true, data: undefined };
    }
  }
  let errorMessage = `Request failed: ${response.status}`;
  try {
    const errorData = await response.json();
    // Handle FastAPI validation errors (array of errors)
    if (Array.isArray(errorData.detail)) {
      errorMessage = errorData.detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
    } else {
      errorMessage = errorData.detail || errorData.message || errorMessage;
    }
  } catch {}
  return { success: false, error: errorMessage };
};

// =============================================================================
// API METHODS
// =============================================================================

export const KnowledgeBaseApi = {
  // -------------------------------------------------------------------------
  // Company Profile
  // -------------------------------------------------------------------------

  async getProfile(): Promise<APIResponse<CompanyProfile>> {
    const response = await fetch(`${API_BASE_URL}/knowledge-base/profile`, {
      method: 'GET',
      headers: createHeaders(),
    });
    return handleResponse<CompanyProfile>(response);
  },

  async updateProfile(data: CompanyProfileUpdateRequest): Promise<APIResponse<CompanyProfile>> {
    const response = await fetch(`${API_BASE_URL}/knowledge-base/profile`, {
      method: 'PUT',
      headers: createHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<CompanyProfile>(response);
  },

  // -------------------------------------------------------------------------
  // Setup Progress
  // -------------------------------------------------------------------------

  async getSetupProgress(): Promise<APIResponse<SetupProgress>> {
    const response = await fetch(`${API_BASE_URL}/knowledge-base/setup-progress`, {
      method: 'GET',
      headers: createHeaders(),
    });
    return handleResponse<SetupProgress>(response);
  },

  // -------------------------------------------------------------------------
  // Documents
  // -------------------------------------------------------------------------

  async listDocuments(params?: {
    category?: CompanyDocumentCategory;
    status?: CompanyDocumentStatus;
    search?: string;
    page?: number;
    page_size?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<APIResponse<DocumentListResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order);

    const url = `${API_BASE_URL}/knowledge-base/documents${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: createHeaders(),
    });
    return handleResponse<DocumentListResponse>(response);
  },

  async getDocument(documentId: string): Promise<APIResponse<CompanyDocument>> {
    const response = await fetch(`${API_BASE_URL}/knowledge-base/documents/${documentId}`, {
      method: 'GET',
      headers: createHeaders(),
    });
    return handleResponse<CompanyDocument>(response);
  },

  async createDocument(data: CompanyDocumentCreateRequest): Promise<APIResponse<CompanyDocument>> {
    const response = await fetch(`${API_BASE_URL}/knowledge-base/documents`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<CompanyDocument>(response);
  },

  async updateDocument(documentId: string, data: CompanyDocumentUpdateRequest): Promise<APIResponse<CompanyDocument>> {
    const response = await fetch(`${API_BASE_URL}/knowledge-base/documents/${documentId}`, {
      method: 'PUT',
      headers: createHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<CompanyDocument>(response);
  },

  async deleteDocument(documentId: string): Promise<APIResponse<void>> {
    const response = await fetch(`${API_BASE_URL}/knowledge-base/documents/${documentId}`, {
      method: 'DELETE',
      headers: createHeaders(),
    });
    return handleResponse<void>(response);
  },

  async bulkUploadDocuments(documents: CompanyDocumentCreateRequest[]): Promise<APIResponse<BulkUploadResponse>> {
    const response = await fetch(`${API_BASE_URL}/knowledge-base/documents/bulk`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify({ documents }),
    });
    return handleResponse<BulkUploadResponse>(response);
  },

  // -------------------------------------------------------------------------
  // Knowledge Retrieval
  // -------------------------------------------------------------------------

  async retrieveKnowledge(request: KnowledgeRetrievalRequest): Promise<APIResponse<KnowledgeRetrievalResponse>> {
    const response = await fetch(`${API_BASE_URL}/knowledge-base/retrieve`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(request),
    });
    return handleResponse<KnowledgeRetrievalResponse>(response);
  },

  // -------------------------------------------------------------------------
  // Statistics & Categories
  // -------------------------------------------------------------------------

  async getStats(): Promise<APIResponse<KnowledgeBaseStats>> {
    const response = await fetch(`${API_BASE_URL}/knowledge-base/stats`, {
      method: 'GET',
      headers: createHeaders(),
    });
    return handleResponse<KnowledgeBaseStats>(response);
  },

  async getCategories(): Promise<APIResponse<CategoryInfo[]>> {
    const response = await fetch(`${API_BASE_URL}/knowledge-base/categories`, {
      method: 'GET',
      headers: createHeaders(),
    });
    return handleResponse<CategoryInfo[]>(response);
  },
};

// =============================================================================
// CATEGORY DISPLAY HELPERS
// =============================================================================

export const CATEGORY_GROUPS = {
  'Performance & Track Record': ['past_performance'],
  'Capabilities & Services': ['capability_statement'],
  'Risk & Innovation': ['risk_register', 'innovation_case'],
  'Social Value & Capacity': ['social_value_evidence', 'capacity_statement'],
  'Company Profile': ['company_overview', 'mission_vision', 'organizational_chart', 'company_history'],
  'Team & Personnel': ['team_cv', 'key_personnel', 'team_structure', 'qualifications'],
  'Experience & Case Studies': ['case_study', 'project_description', 'client_reference', 'testimonial'],
  'Technical Capabilities': ['technical_specification', 'methodology', 'technology_stack', 'integration_capability', 'security_compliance'],
  'Certifications & Accreditations': ['iso_certification', 'industry_accreditation', 'quality_assurance', 'insurance_certificate', 'safety_certification'],
  'Financial Documents': ['financial_statement', 'annual_report', 'bank_reference', 'insurance_coverage'],
  'Policies & Procedures': ['health_safety_policy', 'environmental_policy', 'equality_diversity_policy', 'modern_slavery_statement', 'data_protection_policy', 'business_continuity_plan'],
  'Other': ['other'],
};

export const CATEGORY_DISPLAY_NAMES: Record<CompanyDocumentCategory, string> = {
  company_overview: 'Company Overview',
  mission_vision: 'Mission & Vision',
  organizational_chart: 'Organizational Chart',
  company_history: 'Company History',
  team_cv: 'Team CV / Resume',
  key_personnel: 'Key Personnel',
  team_structure: 'Team Structure',
  qualifications: 'Staff Qualifications',
  case_study: 'Case Study',
  project_description: 'Project Description',
  client_reference: 'Client Reference',
  testimonial: 'Testimonial',
  technical_specification: 'Technical Specification',
  methodology: 'Methodology',
  technology_stack: 'Technology Stack',
  integration_capability: 'Integration Capability',
  security_compliance: 'Security & Compliance',
  iso_certification: 'ISO Certification',
  industry_accreditation: 'Industry Accreditation',
  quality_assurance: 'Quality Assurance',
  insurance_certificate: 'Insurance Certificate',
  safety_certification: 'Safety Certification',
  financial_statement: 'Financial Statement',
  annual_report: 'Annual Report',
  bank_reference: 'Bank Reference',
  insurance_coverage: 'Insurance Coverage',
  health_safety_policy: 'Health & Safety Policy',
  environmental_policy: 'Environmental Policy',
  equality_diversity_policy: 'Equality & Diversity Policy',
  modern_slavery_statement: 'Modern Slavery Statement',
  data_protection_policy: 'Data Protection Policy',
  business_continuity_plan: 'Business Continuity Plan',
  // NEW ENHANCED CATEGORIES
  past_performance: 'Past Performance',
  capability_statement: 'Capability Statement',
  risk_register: 'Risk Register',
  innovation_case: 'Innovation Case Study',
  social_value_evidence: 'Social Value Evidence',
  capacity_statement: 'Capacity Statement',
  other: 'Other',
};

export const CATEGORY_ICONS: Record<string, string> = {
  'Company Profile': '🏢',
  'Team & Personnel': '👥',
  'Experience & Case Studies': '📊',
  'Technical Capabilities': '⚙️',
  'Certifications & Accreditations': '🏆',
  'Financial Documents': '💰',
  'Policies & Procedures': '📋',
  'Other': '📁',
};

export default KnowledgeBaseApi;
